import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';

export type KpiDomain = 'orders' | 'payments' | 'claims' | 'vendors' | 'funding';

/** Query values forwarded to the list endpoint. Arrays cover multi-value
 * filters such as `status[]` used by the vendor/funding list APIs. */
export type KpiQuery = Record<string, string | number | boolean | ReadonlyArray<string>>;

export interface KpiResult {
  totalItems: number;
}

type AnyMeta = {
  pagination?: { totalItems?: number };
};

const extractTotal = (meta: AnyMeta | undefined | null): number => {
  return meta?.pagination?.totalItems ?? 0;
};

/**
 * Compact-mode KPI fetch: hit the canonical admin list endpoint with size=1
 * so the BE returns `meta.pagination.totalItems` without paying the payload
 * cost of the actual row data. team-lead decision option (C): no dedicated
 * dashboard endpoint — every domain reuses its existing list API.
 *
 * The path object keeps `as const` so the indexed access preserves the literal
 * path type the typed client requires.
 *
 * Callers pass an explicit `query` object (e.g. a `status` filter) that the
 * hook forwards as-is alongside `size: 1`.
 */
const fetchKpi = async (
  domain: KpiDomain,
  query: KpiQuery,
): Promise<KpiResult> => {
  const path = (
    {
      orders: '/admin/v1/orders',
      payments: '/api/admin/v1/payments',
      claims: '/api/admin/v1/claims',
      // r5: vendor 입점신청 / funding 캠페인 KPI — 신규 BE endpoint 없이 기존 list API 재사용.
      vendors: '/admin/v1/vendor-applications',
      funding: '/admin/v1/funding-campaigns',
    } as const
  )[domain];
  const [, meta] = await apiClient.request('get', path, {
    params: { query: { ...query, size: 1 } },
  });
  return { totalItems: extractTotal(meta as AnyMeta) };
};

export const dashboardKpiQueryKey = (
  domain: KpiDomain,
  query: KpiQuery,
) => ['admin', 'dashboard-kpi', domain, query] as const;

export const useDashboardKpiQuery = (
  domain: KpiDomain,
  query: KpiQuery,
  options?: { enabled?: boolean },
) =>
  useQuery<KpiResult>({
    queryKey: dashboardKpiQueryKey(domain, query),
    queryFn: () => fetchKpi(domain, query),
    enabled: options?.enabled ?? true,
    staleTime: 60_000,
  });
